import { action, observable, runInAction } from 'mobx'

import { $RootStore } from '../../src/stores/RootStore'
import { ApolloRequesterOptions, Sdk } from '../communication/requester'
import { mergeDeep } from '../utils'

interface $opts { variables?: any, clear?: boolean, cacheLength?: number }

abstract class ModelStore {
  @observable accessor loading = false
  @observable accessor refreshing = false
  abstract data: any
  private initialDataHasBeenSet = false
  cacheLength = 0
  lastUpdatedAt = 0
  rootStore: $RootStore
  fetchMethodName = ''
  saveMethodName = ''
  removeMethodName = ''
  updateMethodName = ''
  defaultFetchVariables: Record<string, any> = {}
  nullableKeys = [] as string[]

  constructor(_data: any, rootStore: $RootStore) {
    this.rootStore = rootStore

    if (rootStore.config?.name === 'dev') {
      setTimeout(() => {
        if (this.initialDataHasBeenSet === false) {
          throw new Error('Initial data not set. Child Class must call this.merge(data) within it\'s constructor.')
        }
      }, 100)
    }
  }

  @action.bound clearCache = () => {
    this.lastUpdatedAt = 0
  }

  // If we have exceeded cache length
  useCached = (cacheLength: number) => {
    return this.lastUpdatedAt + cacheLength > Date.now() ? 'cache-first' : 'network-only'
  }

  // Should return data to merge
  fetchMethod_api = async <T, K extends keyof T>(variables: any, options: ApolloRequesterOptions): Promise<T[K] | null> => {
    const combinedVariables = mergeDeep({ ...this.defaultFetchVariables }, variables || {})

    try {
      const ret = await this.rootStore.communication.requester[this.fetchMethodName as keyof Sdk](
        {
          _id: this.data._id,
          ...combinedVariables,
        } as any,
        options,
      )
      return ret && ret[this.fetchMethodName as keyof typeof ret] || null
    } catch (e) {
      // Error is handled in Communication
      return null
    }
  }

  // Refresh data, used when you pull to refresh
  @action.bound refresh_api = async () => {
    this.refreshing = true
    this.clearCache()

    await this.fetch_api()

    this.refreshing = false
  }

  // Fetch new data and merge with current
  @action.bound fetch_api = async (opts: $opts = {}, alwaysMerge = true) => {
    const { variables } = opts

    this.setLoading(true)

    const updatedAt = this.data.updatedAt
    const fetchPolicy = this.useCached(opts.cacheLength || this.cacheLength)
    const data = await this.fetchMethod_api(variables, { fetchPolicy })

    let hasBeenUpdated = false

    // If data received set on data object
    if (data) {
      hasBeenUpdated = !!data['updatedAt'] && updatedAt !== data['updatedAt']

      if (alwaysMerge || hasBeenUpdated) {
        this.merge(data)
      }
      // Set last updated at for cache
      runInAction(() => {
        this.lastUpdatedAt = Date.now()
      })
    }

    this.setLoading(false)

    return hasBeenUpdated
  }

  // Save new data to server
  @action.bound save_api = async (): Promise<any> => {
    this.setLoading(true)

    try {
      // Execute the save method
      const response = await this.rootStore.communication.requester[this.saveMethodName as keyof Sdk](
        { ...this.saveArgs() },
      )

      this.setLoading(false)

      // Response failed so return
      if (!response) return false

      // Extract data
      const o = response[this.saveMethodName as keyof typeof response]

      // Set data returned by server on object
      this.merge(o)

      // Set last updated at for cache
      runInAction(() => {
        this.lastUpdatedAt = Date.now()
      })

      return this
    } catch (e) {
      this.setLoading(false)
      return false
    }
  }

  // Update data to server
  @action.bound update_api = async (): Promise<any> => {
    this.setLoading(true)

    try {
      const apiMethod = this.rootStore.communication.requester[this.updateMethodName as keyof Sdk]

      if (!apiMethod) {
        throw new Error('No update method found')
      }

      // Execute the save method
      const response = await apiMethod(
        {
          _id: this.data._id,
          ...this.saveArgs(),
        },
      )

      this.setLoading(false)

      // Response failed so return
      if (!response) return false

      // Extract data
      const o = response[this.updateMethodName as keyof typeof response]

      // Set data returned by server on object
      this.merge(o)

      // Set last updated at for cache
      runInAction(() => {
        this.lastUpdatedAt = Date.now()
      })

      return this
    } catch (e) {
      this.setLoading(false)
      return false
    }
  }

  removeMethod_api = async (): Promise<any> => {
    this.setLoading(true)

    // Execute the remove method
    try {
      const response: any = await this.rootStore.communication.requester[this.removeMethodName as keyof Sdk](
        { _id: this.data._id } as any,
      )

      // Response failed so return
      if (!response) return false

      return !!response[this.removeMethodName]
    } catch {
      return false
    } finally {
      this.setLoading(false)
    }
  }

  // Remove record from server
  @action.bound remove_api = async () => {
    this.setLoading(true)

    const deleteCount = await this.removeMethod_api()

    this.setLoading(false)

    return !!deleteCount
  }

  // Merge data on class data object
  @action.bound merge = (o: any) => {
    runInAction(() => {
      if (!this.initialDataHasBeenSet) this.initialDataHasBeenSet = true
    })

    const data = { ...o }
    delete data.__typename // remove __typename

    mergeDeep(this.data, data, this.nullableKeys)
  }

  // Set data on class data object
  @action.bound set(o?: Record<string, any>) {
    const data = { ...o }
    delete data.__typename // remove __typename

    for (const i in data) {
      this.data[i] = data[i]
    }
  }

  // Args for saving remotely
  saveArgs = (): any => {
    return {
      data: this.data,
    }
  }

  @action setLoading = (loading: boolean) => {
    this.loading = loading
  }

  // Called when populating with data from storage
  inflate(o: any) {
    this.merge(o)
  }

  // Used when syncing to storage
  serialize = () => {
    return this.data
  }
}

export default ModelStore
